import React, { Fragment, useCallback } from 'react'
import { ArrowLeftRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from '@/shared/components/ui/resizable'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'

import { ScenarioItemPanel } from './scenario/ScenarioItemPanel'
import { ScenarioToolbar } from './scenario/ScenarioToolbar'
import { useScenarios } from './ScenariosProvider'

export const ScenariosContent = () => {
	const { t } = useTranslation()

	const { panels, addPanel, isLoading } = useScenarios()
	const handleAddPanel = useCallback(() => {
		ampli.scenariosAddPanel({
			panels: panels.length,
		})
		addPanel()
	}, [panels.length, addPanel])
	if (isLoading) return <ContentSkeleton />

	return (
		<ResizablePanelGroup direction='horizontal'>
			{panels?.map((panel, index) => {
				const isLast = index === panels.length - 1

				return (
					<Fragment key={panel.id}>
						<ResizablePanel
							minSize={30}
							className='flex min-w-0 flex-col gap-2 overflow-hidden pt-2'
						>
							<div className='flex h-full min-w-0 flex-col gap-2'>
								<div className='sticky top-0 flex w-full min-w-0 items-center gap-2 px-4'>
									<ScenarioToolbar
										panel={panel}
										// Leave space for the 'Add Panel' button (28px) at the end of the row
										className={cn(isLast && 'w-[calc(100%-28px)]')}
									/>
									{isLast && (
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													variant='primary'
													size='icon'
													onClick={() => {
														ampli.scenariosAddPanel({
															panels: panels.length,
														})
														handleAddPanel()
													}}
												>
													<ArrowLeftRightIcon />
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												{t('scenario.compare.tooltip')}
											</TooltipContent>
										</Tooltip>
									)}
								</div>
								<ScenarioItemPanel panel={panel} />
							</div>
						</ResizablePanel>
						{index < panels.length - 1 && <ResizableHandle withHandle />}
					</Fragment>
				)
			})}
		</ResizablePanelGroup>
	)
}
const ContentSkeleton = () => (
	<div className='h-full space-y-4 p-4'>
		<div className='mb-4 space-y-2'>
			<Skeleton className='h-8 w-full' />
			<div className='flex gap-2'>
				<Skeleton className='h-8 w-1/2' />
				<Skeleton className='h-8 w-1/2' />
			</div>
		</div>
		<div className='space-y-2 pb-4'>
			<Skeleton className='h-40 w-full' />
			<Skeleton className='h-40 w-full' />
			<Skeleton className='h-62 w-full' />
			<Skeleton className='h-96 w-full' />
		</div>
	</div>
)
